import { Avatar, Box, Divider, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

import { RichStepType, type RichStep, type RichStepListProps } from './RichStepList.interface';

import Tag from 'modules/common/components/MUI/Tag';

const NumberedAvatar = styled(Avatar)(({ theme }) => ({
    width: 32,
    height: 32,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
}));

function StepLeading({ step }: Readonly<{ step: RichStep }>) {
    if (step.type === RichStepType.Numbered) {
        return (
            <NumberedAvatar>
                <Typography variant="subtitle1" fontWeight="medium">
                    {step.number}
                </Typography>
            </NumberedAvatar>
        );
    }

    const Icon = step.icon;
    return (
        <Box sx={{ width: 24, height: 24, flexShrink: 0, mt: 0.5 }}>
            <Icon sx={{ width: 24, height: 24, color: 'primary.main' }} />
        </Box>
    );
}

function RichStepItem({ step }: Readonly<{ step: RichStep }>) {
    const alignCenter = step.type === RichStepType.Numbered && !step.description;
    return (
        <Stack direction="row" spacing={2} alignItems={alignCenter ? 'center' : 'flex-start'}>
            <StepLeading step={step} />
            <Stack spacing={0.5}>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Typography variant="h4" sx={{ typography: { xs: 'subtitle2', sm: 'h4' } }}>
                        {step.title}
                    </Typography>
                    {step.tag && <Tag label={step.tag.label} color={step.tag.color} variant="filled" />}
                </Stack>
                {step.description && (
                    <Typography variant="body2" color="text.secondary" component="div">
                        {step.description}
                    </Typography>
                )}
            </Stack>
        </Stack>
    );
}

export default function RichStepList({ steps }: Readonly<RichStepListProps>) {
    return (
        <Stack spacing={2.5} pb={1} divider={<Divider />}>
            {steps.map((step, index) => (
                <RichStepItem
                    key={step.type === RichStepType.Numbered ? step.number : `${index}-${step.title}`}
                    step={step}
                />
            ))}
        </Stack>
    );
}
